import { Fragment, useEffect, useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import axios from "axios";
import config from "../../../../config.json";
import ItemsHistory from "./ItemsHistory";
import swal from "sweetalert";
import "../../../i18n";
import { useTranslation } from "react-i18next";

const ItemsGridHistory = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [isError, setIsError] = useState(false);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${config.dev}/v2/items/retrieve`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`, // Récupérer le token
        },
      });
      const data = response?.data || [];
      setItems(data);
      setFilteredItems(data);
      setIsError(false); // Réinitialise l'état d'erreur
    } catch (err) {
      // Définit un message d'erreur clair
      setError("Failed to fetch items. Please try again later.");
      setIsError(true); // Définit l'état d'erreur à vrai
      console.error("Error fetching items:", err);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    const results = items.filter((item) => {
      // Vérification des détails du produit
      const matchesDetails = item.itemDetails
        ? item.itemDetails.some((detail) =>
            ["itemTitle"].some((key) =>
              detail[key]?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        : false;

      return matchesDetails;
    });

    setFilteredItems(results);
  }, [searchTerm, items]);

  const archivedItems = filteredItems.filter((item) => item?.isArchived);
  const selectedCount = archivedItems.filter((item) =>
    selectedIds.includes(item._id)
  ).length;
  const areAllSelected =
    archivedItems.length > 0 &&
    archivedItems.every((item) => selectedIds.includes(item._id));

  useEffect(() => {
    const validIds = items.map((item) => item?._id).filter(Boolean);
    setSelectedIds((prev) => prev.filter((id) => validIds.includes(id)));
  }, [items]);

  const toggleSelectOne = (id, checked) => {
    setSelectedIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      const ids = archivedItems.map((item) => item._id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
      return;
    }
    const ids = new Set(archivedItems.map((item) => item._id));
    setSelectedIds((prev) => prev.filter((id) => !ids.has(id)));
  };

  const restoreById = (id) => axios.put(`${config.dev}/v2/items/restore/${id}`);
  const deleteById = (id) => axios.delete(`${config.dev}/v2/items/delete/${id}`);

  const handleRestoreSelected = async () => {
    const ids = archivedItems.filter((item) => selectedIds.includes(item._id)).map((item) => item._id);
    if (ids.length === 0) return;
    const ok = await swal({ title: "Are you sure?", text: `Restore ${ids.length} selected items?`, icon: "warning", buttons: true });
    if (!ok) return;
    await Promise.all(ids.map(restoreById));
    setSelectedIds([]);
    fetchItems();
  };

  const handleDeleteSelected = async () => {
    const ids = archivedItems.filter((item) => selectedIds.includes(item._id)).map((item) => item._id);
    if (ids.length === 0) return;
    const ok = await swal({ title: "Are you sure?", text: `Delete ${ids.length} selected items forever?`, icon: "warning", buttons: true, dangerMode: true });
    if (!ok) return;
    await Promise.all(ids.map(deleteById));
    setSelectedIds([]);
    fetchItems();
  };

  return (
    <Fragment>
      <PageTitle activeMenu={t("items")} motherMenu={t("history")} />
      <div className="row">
        <div className="col-lg-6 col-md-12 col-sm-12">
          <input
            type="text"
            className="form-control input-rounded"
            placeholder={t("searchByTitle")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={filteredItems?.length != null}
            disabled={!items?.length}
          />
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="form-check m-0">
          <input
            type="checkbox"
            id="select-all-items"
            className="form-check-input"
            checked={areAllSelected}
            onChange={(e) => toggleSelectAll(e.target.checked)}
            disabled={archivedItems.length === 0}
          />
          <label className="form-check-label ms-2" htmlFor="select-all-items">
            Select all ({archivedItems.length})
          </label>
        </div>
        <div>
          <button className="btn btn-success btn-sm me-2" onClick={handleRestoreSelected} disabled={selectedCount === 0}>
            Restore Selected ({selectedCount})
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected} disabled={selectedCount === 0}>
            Delete Selected ({selectedCount})
          </button>
        </div>
      </div>
      <div className="row mt-5">
        {filteredItems.length === 0 ? (
          <div className="col-12">
            {/* Message d'erreur affiché dans une alerte stylisée */}
            <div className="alert alert-warning" role="alert">
              {t("noDataFound")} <i className="fa fa-spinner" />
            </div>
          </div>
        ) : (
          // Affiche les produits sous forme de grille
          filteredItems.map((item) => (
            <ItemsHistory
              fetchItems={fetchItems}
              key={item._id}
              item={item}
              selected={selectedIds.includes(item._id)}
              onToggleSelect={toggleSelectOne}
            />
          ))
        )}
      </div>
    </Fragment>
  );
};

export default ItemsGridHistory;
